// sapi-admin/src/components/AdminNotificationBell.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { auth, db } from '../firebase/config';
import { doc, setDoc } from 'firebase/firestore';
import {
  Bell, BellOff, ShoppingBag, DollarSign, AlertTriangle,
  Zap, Package, Users, Tag, Image as ImageIcon, Megaphone, Store
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useSound } from '../contexts/SoundContext';
import { useNotificationSound } from '../hooks/useNotificationSound';
import { useAdminNotifications } from '../hooks/useAdminNotifications';
import { Capacitor } from '@capacitor/core';
import { Badge } from '@capawesome/capacitor-badge';
import { App } from '@capacitor/app';


let PushNotifications = null;
if (Capacitor.isNativePlatform()) {
  try {
    PushNotifications = require('@capacitor/push-notifications').PushNotifications;
  } catch (e) {
    console.warn('Plugin PushNotifications non disponible:', e.message);
  }
}

// ─── Icon mapping ──────────────────────────────────────────────────────────
const getIconForType = (type) => {
  switch (type) {
    case 'new_order':
    case 'order_created':         return <ShoppingBag size={14} className="text-emerald-400"/>;
    case 'pi_sell_request':
    case 'pi_sell_pending':       return <DollarSign size={14} className="text-orange-400" />;
    case 'pi_sell_approved':
    case 'pi_sell_confirmed':     return <DollarSign size={14} className="text-green-400" />;
    case 'store_registration_pending': return <Store size={14} className="text-blue-400" />;
    case 'store_activated':       return <Store size={14} className="text-violet-400" />;
    case 'eneo_recharge_success':
    case 'eneo_recharge_admin':   return <Zap size={14} className="text-cyan-400" />;
    case 'order_status_updated':  return <Package size={14} className="text-purple-400" />;
    case 'low_stock':             return <AlertTriangle size={14} className="text-red-400" />;
    case 'new_user':              return <Users size={14} className="text-pink-400" />;
    case 'new_coupon':            return <Tag size={14} className="text-emerald-400" />;
    case 'new_banner':            return <ImageIcon size={14} className="text-green-400" />;
    case 'new_announcement':      return <Megaphone size={14} className="text-blue-400" />;
    default:                      return <Bell size={14} className="text-amber-400" />;
  }
};

const getLinkForType = (type) => {
  switch (type) {
    case 'new_order':
    case 'order_created':
    case 'order_status_updated':        return '/orders';
    case 'pi_sell_request':
    case 'pi_sell_pending':
    case 'pi_sell_approved':            return '/pi-sell-requests';
    case 'store_registration_pending':
    case 'store_registration_approved': return '/pi-stores';
    case 'eneo_recharge_success':
    case 'eneo_recharge_admin':         return '/eneo-transactions';
    case 'low_stock':                   return '/products';
    case 'new_user':                    return '/users';
    case 'new_coupon':                  return '/coupons';
    case 'new_banner':                  return '/banners';
    case 'new_announcement':            return '/announcements';
    default:                            return '/';
  }
};

export default function AdminNotificationBell({ isOnline = true }) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const [isNativePlatform] = useState(Capacitor.isNativePlatform());

  const buttonRef        = useRef(null);
  const dropdownRef      = useRef(null);
  const audioUnlockedRef = useRef(false);

  const { soundEnabled } = useSound();
  const { playSound }    = useNotificationSound(soundEnabled);

  const { notifications, unreadCount, markAsRead, markAllAsRead, loading } =
    useAdminNotifications({ onNewUnread: playSound });

  // ─── Mise à jour du badge (natif + web) ────────────────────────────────
  const updateBadge = useCallback(async (count) => {
    try {
      if (Capacitor.isNativePlatform()) {
        // ✅ Plugin correct pour le badge de l'icône (launcher)
        if (count > 0) {
          await Badge.set({ count });
        } else {
          await Badge.clear();
        }
        console.log(`✅ Badge natif mis à jour (Badge plugin): ${count}`);
      } else if ('setAppBadge' in navigator) {
        if (count > 0) {
          await navigator.setAppBadge(count);
        } else {
          await navigator.clearAppBadge();
        }
        console.log(`✅ Badge web mis à jour: ${count}`);
      }
    } catch (error) {
      console.warn('⚠️ Erreur mise à jour badge:', error);
    }
  }, []);

  useEffect(() => {
    if (!isNativePlatform) return;
    
    // Synchroniser le badge natif avec Firestore au démarrage
    const syncBadge = async () => {
        try {
            // Lire le badge stocké dans les prefs natives (via Capacitor App)
            // Ou simplement utiliser unreadCount de Firestore
            const { Badge } = await import('@capawesome/capacitor-badge');
            const { count } = await Badge.get();
            
            // Si le badge natif est différent de Firestore, mettre à jour
            if (count !== unreadCount) {
                await updateBadge(unreadCount);
            }
        } catch (e) {
            console.warn('Badge sync error:', e);
        }
    };
    
    syncBadge();
}, [isNativePlatform, unreadCount, updateBadge]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listener;
    const setup = async () => {
      listener = await App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          // App passe en arrière-plan → réappliquer le badge natif
          updateBadge(unreadCount);
        }
      });
    };
    setup();

    return () => {
      listener?.remove();
    };
  }, [unreadCount, updateBadge]);

  // ─── Mise à jour du badge à chaque changement de `unreadCount` ─────────
  useEffect(() => {
    if (!loading) {
      updateBadge(unreadCount);
    }
  }, [unreadCount, loading, updateBadge]);

  // ─── Mise à jour initiale une fois les notifications chargées ──────────
  useEffect(() => {
    if (!loading) {
      updateBadge(unreadCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // ─── Setup Push Notifications ──────────────────────────────────────────
  const saveFcmTokenToFirestore = useCallback(async (token) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      await setDoc(
        doc(db, 'users', user.uid, 'fcmTokens', token),
        { token, platform: 'android_native', createdAt: new Date().toISOString(), lastUsed: new Date().toISOString() },
        { merge: true }
      );
    } catch (error) {
      console.error('FCM token save error:', error);
    }
  }, []);

  useEffect(() => {
    if (!isNativePlatform || !PushNotifications) return;
    const setup = async () => {
      try {
        let perm = await PushNotifications.checkPermissions();
        if (perm.receive === 'prompt') perm = await PushNotifications.requestPermissions();
        if (perm.receive !== 'granted') return;

        await PushNotifications.register();
        await PushNotifications.addListener('pushNotificationReceived', () => playSound());
        await PushNotifications.addListener('pushNotificationActionPerformed', () => {
          window.location.href = '/notifications';
        });
        await PushNotifications.addListener('registration', (token) => {
          saveFcmTokenToFirestore(token.value);
        });
      } catch (error) {
        console.error('Push setup error:', error);
      }
    };
    setup();
    return () => PushNotifications?.removeAllListeners();
  }, [isNativePlatform, playSound, saveFcmTokenToFirestore]);

  // ─── Handlers ─────────────────────────────────────────────────────────
  const handleBellClick = useCallback(() => {
    if (!isOnline) return;
    if (!audioUnlockedRef.current) {
      const silent = new Audio('/sounds/notification.mp3');
      silent.volume = 0;
      silent.play().catch(() => {});
      audioUnlockedRef.current = true;
    }
    setIsOpen(v => !v);
  }, [isOnline]);

  const handleMarkAsRead = useCallback((id) => {
    markAsRead(id);
  }, [markAsRead]);

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  // ─── Position du dropdown ────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (buttonRef.current?.contains(e.target) || dropdownRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  const formatTime = (date) => {
    if (!date) return '';
    try { return formatDistanceToNow(date, { addSuffix: true }); }
    catch { return 'recently'; }
  };

  // ─── Rendu du dropdown ────────────────────────────────────────────────
  const renderDropdown = () => (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        right: `${dropdownPosition.right}px`,
        width: '380px',
        maxWidth: 'calc(100vw - 24px)',
        zIndex: 99999,
      }}
      className="bg-[#0d1526] border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <h3 className="text-sm font-semibold text-white">
          Notifications
          {unreadCount > 0 && (
            <span className="ml-2 text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full">
              {unreadCount} new
            </span>
          )}
        </h3>
        {unreadCount > 0 && (
          <button onClick={handleMarkAllAsRead} className="text-xs text-amber-400 hover:text-amber-300 transition">
            Mark all as read
          </button>
        )}
      </div>

      <div className="max-h-96 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="text-center py-10">
            <BellOff size={32} className="mx-auto text-slate-600 mb-2" />
            <p className="text-sm text-slate-500">No notifications yet</p>
          </div>
        ) : (
          notifications.map((notif) => (
            <Link
              key={notif.id}
              to={getLinkForType(notif.type)}
              onClick={() => {
                if (!notif.read) handleMarkAsRead(notif.id);
                setIsOpen(false);
              }}
              className={`flex gap-3 px-4 py-3 border-b border-white/[0.04] hover:bg-white/[0.04] transition-colors ${!notif.read ? 'bg-amber-500/5' : ''}`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${!notif.read ? 'bg-amber-500/15' : 'bg-white/[0.04]'}`}>
                {getIconForType(notif.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-xs leading-snug ${!notif.read ? 'text-white font-medium' : 'text-slate-400'}`}>{notif.title}</p>
                <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{notif.body}</p>
                <p className="text-[9px] text-slate-600 mt-1">{formatTime(notif.createdAt)}</p>
              </div>
              {!notif.read && <div className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0 mt-1.5" />}
            </Link>
          ))
        )}
      </div>

      <div className="px-4 py-2.5 border-t border-white/[0.06] text-center">
        <Link to="/notifications" onClick={() => setIsOpen(false)} className="text-[10px] text-slate-500 hover:text-amber-400 transition">
          View all notifications →
        </Link>
      </div>
    </div>
  );

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleBellClick}
        disabled={!isOnline}
        className={`relative p-2 rounded-lg transition-colors ${
          isOnline
            ? 'hover:bg-white/[0.06] text-slate-400 hover:text-white'
            : 'text-slate-600 cursor-not-allowed opacity-50'
        }`}
        title={isOnline && unreadCount > 0 ? `${unreadCount} notification(s) non lue(s)` : 'Aucune notification'}
      >
        {unreadCount > 0 && isOnline ? (
          <>
            <Bell size={18} className="text-amber-400" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center ring-1 ring-[#0a0f1e]">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          </>
        ) : (
          <BellOff size={18} className="text-slate-500" />
        )}
      </button>

      {!isOnline && <span className="absolute -bottom-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />}

      {isOpen && ReactDOM.createPortal(renderDropdown(), document.body)}
    </div>
  );
}